import {
  mcpSettingDtoSchema,
  mcpStatusDtoSchema,
  myConnectionListDtoSchema,
  orgConnectionListDtoSchema,
  type McpSettingDto,
  type McpStatusDto,
  type MyConnectionListDto,
  type OrgConnectionListDto,
} from '@lib/dto/mcp.dto'
import type { UpdateMcpSettingInput } from '@lib/input/mcp.input'
import type { Uuid } from '@lib/validators/params'
import type { ApiClient } from '@/helpers/apiClient'
import type { IConnectedAppsService } from '@/types/ConnectedAppsService.types'

function tenantPath(tenantId: Uuid): string {
  return `/dashboard/tenants/${encodeURIComponent(tenantId)}/mcp`
}

/**
 * AI app connections: the person's own, an organization's (for its Owners,
 * or staff viewing as it), and the organization's MCP setting. Bound to a
 * session's client (whether MCP is on is public: any client will do).
 */
export class ConnectedAppsService implements IConnectedAppsService {
  constructor(private readonly client: ApiClient) {}

  status(): Promise<McpStatusDto> {
    return this.client.get('/mcp/status', mcpStatusDtoSchema)
  }

  mine(): Promise<MyConnectionListDto> {
    return this.client.get('/dashboard/mcp/connections', myConnectionListDtoSchema)
  }

  disconnect(connectionId: Uuid): Promise<void> {
    return this.client.del(`/dashboard/mcp/connections/${encodeURIComponent(connectionId)}`)
  }

  orgConnections(tenantId: Uuid): Promise<OrgConnectionListDto> {
    return this.client.get(`${tenantPath(tenantId)}/connections`, orgConnectionListDtoSchema)
  }

  removeFromOrg(tenantId: Uuid, connectionId: Uuid): Promise<void> {
    return this.client.del(`${tenantPath(tenantId)}/connections/${encodeURIComponent(connectionId)}`)
  }

  setting(tenantId: Uuid): Promise<McpSettingDto> {
    return this.client.get(`${tenantPath(tenantId)}/setting`, mcpSettingDtoSchema)
  }

  updateSetting(tenantId: Uuid, input: UpdateMcpSettingInput): Promise<McpSettingDto> {
    return this.client.patch(`${tenantPath(tenantId)}/setting`, input, mcpSettingDtoSchema)
  }
}
